import type { Game } from "@rosette/ts/src/royalur/Game.js";
import type { RollDataPoint } from "./RollDistributionGraph.js";
import { RollDistributionGraph } from "./RollDistributionGraph.js";
import { RolledGameState } from "@rosette/ts/src/royalur/engine/state/RolledGameState.js";
import { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import { DARK_COLOUR, LIGHT_COLOUR } from "./analysisConstants.js";
import type { LobbyPlayer } from "@rosette/ts/src/business/lobby/LobbyPlayer.js";
import { HumanLobbyPlayer } from "@rosette/ts/src/business/lobby/HumanLobbyPlayer.js";


export const rollNames = [
    "zero",
    "one",
    "two",
    "three",
    "four",
];

export const rollPluralNames = [
    "zeroes",
    "ones",
    "twos",
    "threes",
    "fours",
];


function getDisplayPlayerName(player: LobbyPlayer, playerType: PlayerType): string {
    if (player instanceof HumanLobbyPlayer && player.isAnonymous())
        return playerType.getName();

    return player.getLongName();
}


interface GameRollDistributionProps {
    className?: string;
    game: Game;
    light: LobbyPlayer;
    dark: LobbyPlayer;
}


export function GameRollDistribution({ className, game, light, dark }: GameRollDistributionProps) {
    const rollProbs = game.getDice().getRollProbabilities();

    // Count frequencies of the rolls of each player.
    let lightRollCount: number = 0;
    let darkRollCount: number = 0;
    const lightRollCounts: number[] = Array.from({ length: rollProbs.length }, () => 0);
    const darkRollCounts: number[] = Array.from({ length: rollProbs.length }, () => 0);
    for (const state of game.getStates()) {
        if (!(state instanceof RolledGameState))
            continue;

        const roll = state.getRoll();
        if (state.getTurn() === PlayerType.LIGHT) {
            lightRollCount += 1;
            lightRollCounts[roll.value()] += 1;
        } else {
            darkRollCount += 1;
            darkRollCounts[roll.value()] += 1;
        }
    }

    // Format data for graphing.
    const data: RollDataPoint[] = [];
    for (let roll = 0; roll < rollProbs.length; ++roll) {
        data.push({
            roll,
            light: lightRollCount > 0 ? lightRollCounts[roll] / lightRollCount : 0,
            dark: darkRollCount > 0 ? darkRollCounts[roll] / darkRollCount : 0,
        });
    }

    return (
        <RollDistributionGraph
            className={className}
            graphClassName="shadow-md"
            rollProbs={rollProbs}
            data={data}
            colours={{
                light: LIGHT_COLOUR,
                dark: DARK_COLOUR,
            }}
            renderTooltip={({ roll, group }) => {
                let name, count, ratio;
                if (group === "light") {
                    name = getDisplayPlayerName(light, PlayerType.LIGHT);
                    count = lightRollCounts[roll];
                    ratio = lightRollCount > 0 ? count / lightRollCount : 0;
                } else {
                    name = getDisplayPlayerName(dark, PlayerType.DARK);
                    count = darkRollCounts[roll];
                    ratio = darkRollCount > 0 ? count / darkRollCount : 0;
                }
                const rollName = (count !== 1 ? rollPluralNames : rollNames)[roll];
                return (
                    <>
                        <div className="p-1">
                            <div className="block pb-2">
                                <span className="font-bold">
                                    {`${name} rolled ${count} ${rollName}`}
                                </span>
                            </div>
                            <div className="block">
                                <span>
                                    {`(${(100 * ratio).toFixed(0)}% of their rolls)`}
                                </span>
                            </div>
                        </div>
                    </>
                );
            }} />
    );
}
